import { cn } from '@/lib/utils'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import * as React from 'react'

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  variant?: 'default' | 'neutral'
  showUnselectIcon?: boolean
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant = 'default', showUnselectIcon = false, checked, ...props }, ref) => {
  const getIndicatorIcon = () => {
    if (showUnselectIcon) {
      return <NeutralUnselectIcon />
    }
    if (variant === 'neutral') {
      return <NeutralCheckedIcon />
    }
    return <CheckedIcon />
  }

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      tabIndex={0}
      className={cn(
        'peer relative shrink-0 focus:outline focus:outline-[2px] focus:outline-neutral-75 disabled:opacity-50',
        variant === 'default' && 'h-3 w-3',
        variant === 'neutral' && 'h-4 w-4 rounded',
        className,
      )}
      checked={showUnselectIcon ? true : checked}
      {...props}
    >
      {variant === 'default' ? <UncheckedIcon /> : <NeutralUncheckedIcon />}
      <CheckboxPrimitive.Indicator
        className={cn('absolute top-0 flex items-center justify-center text-current')}
      >
        {getIndicatorIcon()}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
})

Checkbox.displayName = CheckboxPrimitive.Root.displayName

const UncheckedIcon = () => (
  <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='0.5' y='0.5' width='11' height='11' stroke='#AEA9B4' />
  </svg>
)

const CheckedIcon = () => (
  <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='0.5' y='0.5' width='11' height='11' stroke='#AEA9B4' />
    <rect x='2' y='2' width='8' height='8' fill='#786E96' />
  </svg>
)

const NeutralUncheckedIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='1.5' y='1.5' width='13' height='13' rx='1.5' stroke='#A0A0A0' />
  </svg>
)

const NeutralCheckedIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='1.5' y='1.5' width='13' height='13' rx='1.5' fill='#404040' stroke='#404040' />
    <path
      d='M6.00003 10.2824L12.073 4.19969C12.211 4.06135 12.3865 3.99219 12.5995 3.99219C12.8127 3.99219 12.9885 4.06119 13.127 4.19919C13.2654 4.33719 13.3345 4.51277 13.3345 4.72594C13.3345 4.93927 13.2654 5.11519 13.127 5.25369L6.63278 11.7382C6.45194 11.919 6.24103 12.0094 6.00003 12.0094C5.75903 12.0094 5.54811 11.919 5.36728 11.7382L2.87303 9.25369C2.73469 9.11569 2.66553 8.94019 2.66553 8.72719C2.66553 8.51402 2.73453 8.33819 2.87253 8.19969C3.01053 8.06135 3.18611 7.99219 3.39928 7.99219C3.61261 7.99219 3.78853 8.06135 3.92703 8.19969L6.00003 10.2824Z'
      fill='white'
    />
  </svg>
)

const NeutralUnselectIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='1.5' y='1.5' width='13' height='13' rx='1.5' fill='#404040' stroke='#404040' />
    <path d='M13 8L3 8' stroke='white' strokeWidth='1.5' strokeLinecap='round' />
  </svg>
)
